import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { AbstractDynamicReportViewComponent } from '../../abstract/abstract-dynamic-report-view.component';
import { GroupedDataRecordModel, ReportRecord } from '../../models/report.model';
import { groupByProperty, mapImageColorToRecordItems, sortByParentPosition } from '../../shared';
import { EditableFieldsService } from '../editable-fields.service';
import { togglePointFunction } from '@cbre/feature-toggles';

@Component({
  selector: 'rep-condensed-view',
  templateUrl: './condensed-view.component.html',
  styleUrls: ['./condensed-view.component.scss'],
  providers: [EditableFieldsService],
  standalone: false,
})
export class CondensedViewComponent extends AbstractDynamicReportViewComponent implements OnChanges {
  @Input() maxRecordsPerPage = 6;
  @Output() removeRecord: EventEmitter<ReportRecord> = new EventEmitter<ReportRecord>();

  public isNewCondensedView = false;

  reportItems: GroupedDataRecordModel[];

  ngOnChanges({ columns, currentView, reportLocale, reportData, reportOptions }: SimpleChanges) {
    super.ngOnChanges({ columns, currentView, reportLocale, reportData });
    // update report list if report data or condensed options are changed
    if (reportData || this._isCondensedOptionsChanged(reportOptions)) {
      this.makeReportList();
    }
  }

  /**
   * Checks if report options values involved in condensed view are changed
   * @param reportOptions
   * @returns
   */
  private _isCondensedOptionsChanged(reportOptions: SimpleChange) {
    // check if groupByBuilding is changed
    return reportOptions?.currentValue?.groupByBuilding !== reportOptions?.previousValue?.groupByBuilding;
  }

  private makeReportList() {
    const { groupByBuilding } = this.reportOptions;
    const { records } = this.reportData;
    const mappedRecords = groupByBuilding ? groupByProperty(records) : sortByParentPosition(records);
    this.reportItems = mapImageColorToRecordItems(mappedRecords);
    // TODO(ppt): add configurations for highlights and reps (contacts) hiding
    // Validation added because sometimes we are getting report views which are not configured.
    // Apply PDF logic
    this.applyPdfSplittingToReportItems();
  }

  @togglePointFunction('CondensedReportFlag', 'paginateReportItemsForPdf')
  protected applyPdfSplittingToReportItems() {
    if (this.isPDF) {
      this.isNewCondensedView = false;
      this.reportItems = this.splitPdfRecords(this.reportItems);
    }
  }

  protected paginateReportItemsForPdf() {
    this.isNewCondensedView = true;
  }

  private splitPdfRecords(reportItems: GroupedDataRecordModel[]): GroupedDataRecordModel[] {
    return reportItems.reduce((acc, reportItem) => {
      return [...acc, ...this.multiplyReportRecords(reportItem)];
    }, []);
  }

  /**
   * Receives a report item and recursively clones and multiply the report item
   * depending on the max number of records inside it, returning an array of report
   * items.
   */
  private multiplyReportRecords(reportItem: GroupedDataRecordModel): GroupedDataRecordModel[] {
    let resultData = [];
    if (reportItem.data.length > this.maxRecordsPerPage) {
      const reportItemWLeftovers = Object.assign({}, reportItem);
      const tmpReportItem = Object.assign({}, reportItem);
      tmpReportItem.data = [...tmpReportItem.data];
      reportItemWLeftovers.data = tmpReportItem.data.splice(6);
      resultData.push(tmpReportItem, ...this.multiplyReportRecords(reportItemWLeftovers));
    } else {
      resultData = [reportItem];
    }
    return resultData;
  }

  removeRecordFromReport(reportItem: ReportRecord, recordIndex: number) {
    const recordId = reportItem.recordId ? reportItem.recordId : reportItem.data[recordIndex].recordId;
    this.removeRecord.emit({ ...reportItem, recordId });
  }
}
